import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Plus, Trash2, Share2, Copy, Check, ExternalLink } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useUser } from 'context/user/User';
import otpUsersApi from 'api/requests/otpUsers';
import orderStationsApi from 'api/requests/orderStations';
import BottomSheet from 'components/_UI/BottomSheet';
import DateTimeInput from 'components/_UI/DateTimeInput';
import colors from 'theme/colors';

const CLIENT_URL = import.meta.env.VITE_CLIENT_URL || window.location.origin;

function otpLoginUrl(otp) {
    return `${CLIENT_URL}/acesso?otp=${otp}`;
}

function todayMidnight() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

function computeExpiresAt(preset, customValue) {
    if (preset === 'none') return null;
    if (preset === 'custom') {
        if (!customValue) return null;
        const d = new Date(customValue);
        if (isNaN(d.getTime()) || d <= new Date()) return null;
        return customValue; // already YYYY-MM-DDTHH:mm local time, no timezone
    }
    const d = new Date(Date.now() + { '1d': 1, '7d': 7 }[preset] * 86_400_000);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatExpiry(expiresAt) {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    if (isNaN(expiry)) return null;
    if (expiry < new Date()) return { label: 'Expirado', expired: true };
    const pad = n => String(n).padStart(2, '0');
    const day = pad(expiry.getDate());
    const month = pad(expiry.getMonth() + 1);
    const hours = pad(expiry.getHours());
    const mins = pad(expiry.getMinutes());
    return { label: `Expira ${day}/${month} às ${hours}h${mins !== '00' ? mins : ''}`, expired: false };
}

export default function AdminPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;

    // ── Stations ──
    const [stations, setStations] = useState([]);
    const [stationsLoading, setStationsLoading] = useState(true);
    const [addStationSheet, setAddStationSheet] = useState(false);
    const [newStationName, setNewStationName] = useState('');
    const [creatingStation, setCreatingStation] = useState(false);
    const [deleteStationTarget, setDeleteStationTarget] = useState(null);
    const [deletingStation, setDeletingStation] = useState(false);

    // ── OTP users ──
    const [otpUsers, setOtpUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addSheet, setAddSheet] = useState(false);
    const [newName, setNewName] = useState('');
    const [newViewType, setNewViewType] = useState('tabs');
    const [newStationId, setNewStationId] = useState('');
    const [newExpiryPreset, setNewExpiryPreset] = useState('none'); // 'none'|'1d'|'7d'|'30d'|'custom'
    const [newCustomExpiry, setNewCustomExpiry] = useState('');
    const [creating, setCreating] = useState(false);
    const [createdWaiter, setCreatedWaiter] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [qrWaiter, setQrWaiter] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        if (!slug) return;
        orderStationsApi.list(slug)
            .then(({ data }) => setStations(data.data || data || []))
            .catch(() => {})
            .finally(() => setStationsLoading(false));
        otpUsersApi.list(slug)
            .then(({ data }) => setOtpUsers(data.data || data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [slug]);

    // ── Station handlers ──
    async function handleCreateStation() {
        if (!newStationName.trim()) return;
        setCreatingStation(true);
        try {
            const { data } = await orderStationsApi.create(slug, newStationName.trim());
            setStations(prev => [...prev, data]);
            setNewStationName('');
            setAddStationSheet(false);
        } catch {}
        finally { setCreatingStation(false); }
    }

    async function handleDeleteStation() {
        if (!deleteStationTarget) return;
        setDeletingStation(true);
        try {
            await orderStationsApi.delete(slug, deleteStationTarget.id);
            setStations(prev => prev.filter(s => s.id !== deleteStationTarget.id));
            setDeleteStationTarget(null);
        } catch {}
        finally { setDeletingStation(false); }
    }

    // ── OTP user handlers ──
    async function handleCreate() {
        if (!newName.trim()) return;
        if (newViewType === 'station' && !newStationId) return;
        setCreating(true);
        try {
            const payload = { name: newName.trim(), view_type: newViewType };
            if (newViewType === 'station') payload.order_station_id = Number(newStationId);
            const expiresAt = computeExpiresAt(newExpiryPreset, newCustomExpiry);
            if (expiresAt) payload.expires_at = expiresAt;
            const { data } = await otpUsersApi.create(slug, payload);
            setOtpUsers(prev => [...prev, data]);
            setCreatedWaiter(data);
            setNewName('');
            setNewViewType('tabs');
            setNewStationId('');
            setNewExpiryPreset('none');
            setNewCustomExpiry('');
        } catch {}
        finally { setCreating(false); }
    }

    async function handleDelete() {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await otpUsersApi.delete(slug, deleteTarget.id);
            setOtpUsers(prev => prev.filter(w => w.id !== deleteTarget.id));
            setDeleteTarget(null);
        } catch {}
        finally { setDeleting(false); }
    }

    function handleShare(waiter) {
        const url = otpLoginUrl(waiter.otp_code);
        if (navigator.share) {
            navigator.share({ title: `Acesso — ${waiter.name}`, url });
        } else {
            handleCopy(waiter);
        }
    }

    function handleCopy(waiter) {
        navigator.clipboard?.writeText(otpLoginUrl(waiter.otp_code));
        setCopiedId(waiter.id);
        setTimeout(() => setCopiedId(null), 2000);
    }

    function handleCloseAddSheet() {
        setAddSheet(false);
        setCreatedWaiter(null);
        setNewName('');
        setNewViewType('tabs');
        setNewStationId('');
        setNewExpiryPreset('none');
        setNewCustomExpiry('');
    }

    const canCreate = newName.trim() && (newViewType === 'tabs' || (newViewType === 'station' && newStationId));

    return (
        <Page>
            {/* ── Estações de Pedido ── */}
            <Section>
                <SectionHeader>
                    <SectionTitle>Estações de Pedido</SectionTitle>
                    <AddBtn onClick={() => setAddStationSheet(true)}>
                        <Plus size={16} strokeWidth={2.5} /> Nova
                    </AddBtn>
                </SectionHeader>

                {stationsLoading && <LoadingText>Carregando...</LoadingText>}

                {!stationsLoading && stations.length === 0 && (
                    <EmptyState>
                        <EmptyTitle>Nenhuma estação cadastrada</EmptyTitle>
                        <EmptyHint>Crie estações para rotear pedidos (ex: Cozinha, Bar).</EmptyHint>
                    </EmptyState>
                )}

                {!stationsLoading && stations.length > 0 && (
                    <StationList>
                        {stations.map(s => (
                            <StationRow key={s.id}>
                                <StationName>{s.name}</StationName>
                                <ActionBtn
                                    title="Excluir estação"
                                    $danger
                                    onClick={() => setDeleteStationTarget(s)}
                                >
                                    <Trash2 size={16} color={colors.danger} />
                                </ActionBtn>
                            </StationRow>
                        ))}
                    </StationList>
                )}
            </Section>

            {/* ── Usuários OTP ── */}
            <Section>
                <SectionHeader>
                    <SectionTitle>Usuários de Acesso</SectionTitle>
                    <AddBtn onClick={() => setAddSheet(true)}>
                        <Plus size={16} strokeWidth={2.5} /> Novo
                    </AddBtn>
                </SectionHeader>

                {loading && <LoadingText>Carregando...</LoadingText>}

                {!loading && otpUsers.length === 0 && (
                    <EmptyState>
                        <EmptyTitle>Nenhum usuário cadastrado</EmptyTitle>
                        <EmptyHint>Adicione garçons ou operadores de estação.</EmptyHint>
                    </EmptyState>
                )}

                {!loading && otpUsers.length > 0 && (
                    <OtpUserList>
                        {otpUsers.map(w => (
                            <OtpUserCard key={w.id}>
                                <OtpUserInfo>
                                    <OtpUserName>{w.name}</OtpUserName>
                                    <OtpUserMeta>
                                        <OtpUserCode>{w.otp_code}</OtpUserCode>
                                        <ViewBadge $station={w.views[0].type === 'station'}>
                                            {w.views[0].type === 'station'
                                                ? `Estação: ${w.views[0].station?.name || '—'}`
                                                : 'Comandas'}
                                        </ViewBadge>
                                        {(() => {
                                            const expiry = formatExpiry(w.expires_at);
                                            return expiry ? (
                                                <ExpiryBadge $expired={expiry.expired}>{expiry.label}</ExpiryBadge>
                                            ) : null;
                                        })()}
                                    </OtpUserMeta>
                                </OtpUserInfo>
                                <OtpUserActions>
                                    <ActionBtn title="Ver QR Code" onClick={() => setQrWaiter(w)}>
                                        <QrIcon />
                                    </ActionBtn>
                                    <ActionBtn title="Compartilhar" onClick={() => handleShare(w)}>
                                        {copiedId === w.id
                                            ? <Check size={16} color={colors.occasionalGreen} />
                                            : <Share2 size={16} color={colors.night} />}
                                    </ActionBtn>
                                    <ActionBtn
                                        title="Abrir link"
                                        as="a"
                                        href={otpLoginUrl(w.otp_code)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <ExternalLink size={16} color={colors.night} />
                                    </ActionBtn>
                                    <ActionBtn $danger title="Excluir" onClick={() => setDeleteTarget(w)}>
                                        <Trash2 size={16} color={colors.danger} />
                                    </ActionBtn>
                                </OtpUserActions>
                            </OtpUserCard>
                        ))}
                    </OtpUserList>
                )}
            </Section>

            {/* ── Add station sheet ── */}
            <BottomSheet
                isOpen={addStationSheet}
                onClose={() => { setAddStationSheet(false); setNewStationName(''); }}
                title="Nova estação"
                size="small"
            >
                <SheetForm>
                    <FormField>
                        <FormLabel>Nome da estação</FormLabel>
                        <FormInput
                            placeholder="Ex: Cozinha, Bar, Balcão"
                            value={newStationName}
                            autoFocus
                            onChange={e => setNewStationName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !creatingStation && newStationName.trim() && handleCreateStation()}
                        />
                    </FormField>
                    <CreateBtn onClick={handleCreateStation} disabled={!newStationName.trim() || creatingStation}>
                        {creatingStation ? 'Criando...' : 'Criar estação'}
                    </CreateBtn>
                </SheetForm>
            </BottomSheet>

            {/* ── Delete station confirm ── */}
            <BottomSheet
                isOpen={!!deleteStationTarget}
                onClose={() => setDeleteStationTarget(null)}
                title="Excluir estação"
                size="small"
            >
                <DeleteContent>
                    <DeleteMessage>
                        Deseja excluir a estação <strong>{deleteStationTarget?.name}</strong>?
                        Produtos vinculados a ela ficam sem estação.
                    </DeleteMessage>
                    <ConfirmBtns>
                        <BackBtn onClick={() => setDeleteStationTarget(null)}>Cancelar</BackBtn>
                        <DeleteBtn onClick={handleDeleteStation} disabled={deletingStation}>
                            {deletingStation ? 'Excluindo...' : 'Excluir'}
                        </DeleteBtn>
                    </ConfirmBtns>
                </DeleteContent>
            </BottomSheet>

            {/* ── Add OTP user sheet ── */}
            <BottomSheet
                isOpen={addSheet}
                onClose={handleCloseAddSheet}
                title={createdWaiter ? 'Usuário criado' : 'Novo usuário'}
                size="large"
            >
                {!createdWaiter ? (
                    <SheetForm>
                        <FormField>
                            <FormLabel>Nome</FormLabel>
                            <FormInput
                                placeholder="Ex: João Silva"
                                value={newName}
                                autoFocus
                                onChange={e => setNewName(e.target.value)}
                            />
                        </FormField>

                        <FormField>
                            <FormLabel>Tipo de acesso</FormLabel>
                            <ViewTypePills>
                                <ViewTypePill
                                    $active={newViewType === 'tabs'}
                                    onClick={() => { setNewViewType('tabs'); setNewStationId(''); }}
                                    type="button"
                                >
                                    Comandas
                                </ViewTypePill>
                                <ViewTypePill
                                    $active={newViewType === 'station'}
                                    onClick={() => setNewViewType('station')}
                                    type="button"
                                >
                                    Estação
                                </ViewTypePill>
                            </ViewTypePills>
                        </FormField>

                        <FormField>
                            <FormLabel>Validade</FormLabel>
                            <ViewTypePills style={{ flexWrap: 'wrap' }}>
                                {[
                                    { key: 'none', label: 'Sem validade' },
                                    { key: '1d',   label: '1 dia' },
                                    { key: '7d',   label: '7 dias' },
                                    { key: 'custom', label: 'Personalizado' },
                                ].map(({ key, label }) => (
                                    <ViewTypePill
                                        key={key}
                                        $active={newExpiryPreset === key}
                                        onClick={() => {
                                            setNewExpiryPreset(key);
                                            if (key === 'custom' && !newCustomExpiry) {
                                                setNewCustomExpiry(todayMidnight());
                                            }
                                        }}
                                        type="button"
                                    >
                                        {label}
                                    </ViewTypePill>
                                ))}
                            </ViewTypePills>
                            {newExpiryPreset === 'custom' && (
                                <DateTimeInput
                                    value={newCustomExpiry}
                                    onChange={e => setNewCustomExpiry(e.target.value)}
                                    min={todayMidnight()}
                                />
                            )}
                        </FormField>

                        {newViewType === 'station' && (
                            <FormField>
                                <FormLabel>Estação</FormLabel>
                                {stations.length === 0 ? (
                                    <FormHint>Nenhuma estação cadastrada. Crie uma estação primeiro.</FormHint>
                                ) : (
                                    <FormSelect
                                        value={newStationId}
                                        onChange={e => setNewStationId(e.target.value)}
                                    >
                                        <option value="">Selecione uma estação</option>
                                        {stations.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </FormSelect>
                                )}
                            </FormField>
                        )}

                        <CreateBtn onClick={handleCreate} disabled={!canCreate || creating}>
                            {creating ? 'Gerando...' : 'Gerar código de acesso'}
                        </CreateBtn>
                    </SheetForm>
                ) : (
                    <CreatedContent>
                        <CreatedName>{createdWaiter.name}</CreatedName>
                        <ViewBadge $station={createdWaiter.view_type === 'station'} style={{ alignSelf: 'center' }}>
                            {createdWaiter.view_type === 'station'
                                ? `Estação: ${createdWaiter.station?.name || '—'}`
                                : 'Comandas'}
                        </ViewBadge>
                        {(() => {
                            const expiry = formatExpiry(createdWaiter.expires_at);
                            return expiry ? (
                                <ExpiryBadge $expired={expiry.expired} style={{ alignSelf: 'center' }}>
                                    {expiry.label}
                                </ExpiryBadge>
                            ) : null;
                        })()}
                        <OtpDisplay>{createdWaiter.otp_code}</OtpDisplay>
                        <QrWrap>
                            <QRCodeSVG
                                value={otpLoginUrl(createdWaiter.otp_code)}
                                size={180}
                                fgColor={colors.nightDark}
                                bgColor="#fff"
                            />
                        </QrWrap>
                        <ShareRow>
                            <ShareBtn onClick={() => handleShare(createdWaiter)}>
                                <Share2 size={16} /> Compartilhar
                            </ShareBtn>
                            <CopyBtn onClick={() => handleCopy(createdWaiter)}>
                                {copiedId === createdWaiter.id
                                    ? <><Check size={16} /> Copiado!</>
                                    : <><Copy size={16} /> Copiar</>}
                            </CopyBtn>
                            <OpenBtn href={otpLoginUrl(createdWaiter.otp_code)} target="_blank" rel="noopener noreferrer">
                                <ExternalLink size={16} /> Abrir
                            </OpenBtn>
                        </ShareRow>
                        <DoneBtn onClick={handleCloseAddSheet}>Fechar</DoneBtn>
                    </CreatedContent>
                )}
            </BottomSheet>

            {/* ── QR modal ── */}
            <BottomSheet
                isOpen={!!qrWaiter}
                onClose={() => setQrWaiter(null)}
                title={qrWaiter?.name}
                size="large"
            >
                {qrWaiter && (
                    <QrModalContent>
                        <OtpDisplay>{qrWaiter.otp_code}</OtpDisplay>
                        {(() => {
                            const expiry = formatExpiry(qrWaiter.expires_at);
                            return expiry ? (
                                <ExpiryBadge $expired={expiry.expired}>{expiry.label}</ExpiryBadge>
                            ) : null;
                        })()}
                        <QrWrap>
                            <QRCodeSVG
                                value={otpLoginUrl(qrWaiter.otp_code)}
                                size={200}
                                fgColor={colors.nightDark}
                                bgColor="#fff"
                            />
                        </QrWrap>
                        <ShareRow>
                            <ShareBtn onClick={() => handleShare(qrWaiter)}>
                                <Share2 size={16} /> Compartilhar
                            </ShareBtn>
                            <CopyBtn onClick={() => handleCopy(qrWaiter)}>
                                {copiedId === qrWaiter.id
                                    ? <><Check size={16} /> Copiado!</>
                                    : <><Copy size={16} /> Copiar</>}
                            </CopyBtn>
                            <OpenBtn href={otpLoginUrl(qrWaiter.otp_code)} target="_blank" rel="noopener noreferrer">
                                <ExternalLink size={16} /> Abrir
                            </OpenBtn>
                        </ShareRow>
                    </QrModalContent>
                )}
            </BottomSheet>

            {/* ── Delete OTP user confirm ── */}
            <BottomSheet
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                title="Excluir acesso"
                size="small"
            >
                <DeleteContent>
                    <DeleteMessage>
                        Deseja excluir o acesso de <strong>{deleteTarget?.name}</strong>?
                        O código será invalidado imediatamente.
                    </DeleteMessage>
                    <ConfirmBtns>
                        <BackBtn onClick={() => setDeleteTarget(null)}>Cancelar</BackBtn>
                        <DeleteBtn onClick={handleDelete} disabled={deleting}>
                            {deleting ? 'Excluindo...' : 'Excluir'}
                        </DeleteBtn>
                    </ConfirmBtns>
                </DeleteContent>
            </BottomSheet>
        </Page>
    );
}

/* ─── Animations ─── */
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

/* ─── Layout ─── */
const Page = styled.div`display: flex; flex-direction: column; gap: 24px;`;

const Section = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
`;

const AddBtn = styled.button`
    display: flex; align-items: center; gap: 6px; padding: 8px 16px;
    border-radius: 10px; background: ${colors.occasionalPurple}; color: #fff;
    font-size: 14px; font-weight: 600; border: none; cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const LoadingText = styled.p`
    text-align: center; color: ${colors.nightLight}; font-size: 14px;
    padding: 32px; margin: 0;
`;

const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center;
    padding: 40px 24px; text-align: center; gap: 8px;
`;

const EmptyTitle = styled.p`font-weight: 700; font-size: 15px; color: ${colors.nightDark}; margin: 0;`;
const EmptyHint = styled.p`font-size: 13px; color: ${colors.nightLight}; margin: 0;`;

/* ─── Stations list ─── */
const StationList = styled.div`display: flex; flex-direction: column;`;
const StationRow = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; border-bottom: 1px solid ${colors.neutralDark};
    &:last-child { border-bottom: none; }
`;
const StationName = styled.span`font-size: 15px; font-weight: 600; color: ${colors.nightDark};`;

/* ─── OTP users list ─── */
const OtpUserList = styled.div`display: flex; flex-direction: column;`;

const OtpUserCard = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px; gap: 12px; border-bottom: 1px solid ${colors.neutralDark};
    &:last-child { border-bottom: none; }
`;

const OtpUserInfo = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 0;`;
const OtpUserName = styled.span`font-size: 15px; font-weight: 600; color: ${colors.nightDark};`;
const OtpUserMeta = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;
const OtpUserCode = styled.span`
    font-size: 12px; font-weight: 700; color: ${colors.occasionalPurple};
    font-family: monospace; letter-spacing: 1px;
`;
const ViewBadge = styled.span`
    font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px;
    background: ${({ $station }) => $station ? `${colors.occasionalOrange}15` : `${colors.primary}15`};
    color: ${({ $station }) => $station ? colors.occasionalOrange : colors.primary};
`;

const ExpiryBadge = styled.span`
    font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px;
    background: ${({ $expired }) => $expired ? `${colors.danger}15` : `${colors.nightLight}20`};
    color: ${({ $expired }) => $expired ? colors.danger : colors.nightLight};
`;

const OtpUserActions = styled.div`display: flex; align-items: center; gap: 4px; flex-shrink: 0;`;

const ActionBtn = styled.button`
    width: 36px; height: 36px; border-radius: 10px;
    background: ${({ $danger }) => $danger ? `${colors.danger}10` : colors.neutralDark};
    border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: opacity 0.15s; text-decoration: none;
    &:hover { opacity: 0.75; }
`;

/* ─── QR icon ─── */
function QrIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.night} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/>
            <rect x="19" y="14" width="2" height="2"/><rect x="14" y="19" width="2" height="2"/>
            <rect x="17" y="17" width="2" height="5"/><rect x="19" y="19" width="5" height="2"/>
        </svg>
    );
}

/* ─── Forms ─── */
const SheetForm = styled.div`display: flex; flex-direction: column; gap: 14px;`;
const FormField = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const FormLabel = styled.label`font-size: 13px; font-weight: 600; color: ${colors.nightDark};`;
const FormInput = styled.input`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 14px 16px;
    font-size: 16px; outline: none; color: ${colors.nightDark}; background: #fff;
    width: 100%; box-sizing: border-box; transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: ${colors.nightLight}; }
`;
const FormSelect = styled.select`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 14px 16px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; background: #fff;
    width: 100%; box-sizing: border-box; cursor: pointer; transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
`;
const FormHint = styled.p`font-size: 13px; color: ${colors.nightLight}; margin: 0;`;

const ViewTypePills = styled.div`display: flex; gap: 8px;`;
const ViewTypePill = styled.button`
    flex: 1; padding: 11px; border-radius: 10px; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: all 0.15s;
    border: 1.5px solid ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $active }) => $active ? `${colors.occasionalPurple}10` : '#fff'};
    color: ${({ $active }) => $active ? colors.occasionalPurple : colors.night};
`;

const CreateBtn = styled.button`
    padding: 15px; border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 16px; font-weight: 700; border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover:not(:disabled) { opacity: 0.9; }
`;

/* ─── Created / QR ─── */
const CreatedContent = styled.div`display: flex; flex-direction: column; align-items: center; gap: 16px;`;
const QrModalContent = styled.div`display: flex; flex-direction: column; align-items: center; gap: 16px;`;
const CreatedName = styled.p`font-size: 17px; font-weight: 700; color: ${colors.nightDark}; margin: 0;`;
const OtpDisplay = styled.div`
    font-size: 22px; font-weight: 800; letter-spacing: 3px; color: ${colors.occasionalPurple};
    font-family: monospace; background: ${colors.occasionalPurple}10;
    padding: 10px 24px; border-radius: 12px; border: 1.5px solid ${colors.occasionalPurple}30;
`;
const QrWrap = styled.div`
    padding: 16px; background: #fff; border-radius: 14px;
    border: 1.5px solid ${colors.neutralDark};
`;
const ShareRow = styled.div`display: flex; gap: 10px; width: 100%;`;
const ShareBtn = styled.button`
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 13px; border-radius: 10px; font-size: 14px; font-weight: 600;
    background: ${colors.occasionalPurple}15; color: ${colors.occasionalPurple};
    border: 1.5px solid ${colors.occasionalPurple}30; cursor: pointer;
    &:hover { opacity: 0.85; }
`;
const CopyBtn = styled.button`
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 13px; border-radius: 10px; font-size: 14px; font-weight: 600;
    background: ${colors.neutralDark}; color: ${colors.night};
    border: none; cursor: pointer;
    &:hover { opacity: 0.85; }
`;
const OpenBtn = styled.a`
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 13px; border-radius: 10px; font-size: 14px; font-weight: 600;
    background: ${colors.neutralDark}; color: ${colors.night};
    border: none; cursor: pointer; text-decoration: none;
    &:hover { opacity: 0.85; }
`;
const DoneBtn = styled.button`
    width: 100%; padding: 15px; border-radius: 10px; background: ${colors.occasionalPurple};
    color: #fff; font-size: 16px; font-weight: 700; border: none; cursor: pointer;
    &:hover { opacity: 0.9; }
`;

/* ─── Delete confirm ─── */
const DeleteContent = styled.div`display: flex; flex-direction: column; gap: 20px;`;
const DeleteMessage = styled.p`font-size: 15px; color: ${colors.night}; margin: 0; line-height: 1.5;`;
const ConfirmBtns = styled.div`display: flex; gap: 10px;`;
const BackBtn = styled.button`
    flex: 1; padding: 15px; border-radius: 10px; background: transparent;
    color: ${colors.night}; font-size: 16px; font-weight: 700;
    border: 1.5px solid ${colors.neutralDark}; cursor: pointer;
    &:hover { background: ${colors.neutralDark}; }
`;
const DeleteBtn = styled.button`
    flex: 1; padding: 15px; border-radius: 10px; background: ${colors.danger};
    color: #fff; font-size: 16px; font-weight: 700; border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover:not(:disabled) { opacity: 0.9; }
    &:disabled { opacity: 0.5; }
`;
